import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { ChevronRightIcon } from '../../components/ui/Icons';
import type { User, UnlistedStock } from '../../types';

interface UnlistedStocksProps {
    user?: User | null;
}

const UnlistedStocks: React.FC<UnlistedStocksProps> = ({ user }) => {
    const [stocks, setStocks] = useState<UnlistedStock[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchStocks = async () => {
            try {
                const response = await api.get('/unlisted-stocks');
                if (response.data.success) {
                    setStocks(response.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch stocks:', err);
                setError('Failed to load unlisted stocks. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchStocks();
    }, []);

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-trade-primary"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-red-50 text-red-600 p-4 rounded-lg">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
            {/* Breadcrumbs */}
            <nav className="flex items-center space-x-2 text-sm text-slate-500 mb-6">
                <Link to="/grow" className="hover:text-trade-primary transition-colors">Grow</Link>
                <ChevronRightIcon className="w-4 h-4" />
                <span className="text-slate-900 font-medium">Unlisted Stocks</span>
            </nav>

            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Unlisted Stocks</h1>
                    <p className="text-slate-600 mt-2 text-lg">
                        Get access to unlisted shares of a curated list of promising high-growth private companies before they go public.
                    </p>
                </div>
                {(user?.isGrowthAdmin || user?.isUnlistedAdmin) && (
                    <Link to="/admin/unlisted-stocks/new" className="btn btn-primary px-4 py-2 rounded-lg bg-trade-primary text-white hover:bg-blue-700 transition-colors">
                        + Add New Stock
                    </Link>
                )}
            </div>

            {stocks.length === 0 ? (
                <div className="text-center py-16 bg-slate-50 rounded-2xl border border-slate-100">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Coming Soon</h2>
                    <p className="text-slate-600 max-w-md mx-auto">
                        We are currently curating an exclusive list of high-growth unlisted companies.
                        Check back soon for new investment opportunities.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {stocks.map((stock) => (
                        <div key={stock.id} className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 hover:shadow-md transition-all duration-200">
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center space-x-4">
                                    {stock.image_url ? (
                                        <img
                                            src={stock.image_url.startsWith('http') ? stock.image_url : `/api${stock.image_url}`}
                                            alt={stock.name}
                                            className="w-12 h-12 rounded-lg object-cover bg-slate-100"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(stock.name)}&background=random`;
                                            }}
                                        />
                                    ) : (
                                        <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg">
                                            {stock.name.substring(0, 2).toUpperCase()}
                                        </div>
                                    )}
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-900">{stock.name}</h3>
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 mt-1">
                                            {stock.sector}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-lg font-bold text-slate-900">
                                        ₹{stock.price.toLocaleString()}
                                    </p>
                                </div>
                            </div>

                            <div className="text-slate-600 text-sm mb-6 line-clamp-3" dangerouslySetInnerHTML={{ __html: stock.description }} />

                            <Link
                                to={`/grow/unlisted-stocks/${stock.slug || stock.id}`}
                                className="block w-full text-center btn btn-primary py-2.5 bg-trade-primary text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                View Details
                            </Link>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-12 bg-blue-50 border border-blue-100 rounded-xl p-8 text-center">
                <h3 className="text-xl font-bold text-blue-900 mb-2">Don't see what you're looking for?</h3>
                <p className="text-blue-700 mb-6 max-w-2xl mx-auto">
                    We're continuously adding more promising high-growth private companies to this list. Drop a message if you believe there's a promising company that should be on this list.
                </p>
                <button className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-6 rounded-lg transition-colors">
                    Request
                </button>
            </div>
        </div>
    );
};

export default UnlistedStocks;
